<template>
  <v-card class="forgotNewPasswordForm_card">
    <h2 class="forgotNewPasswordForm_title">
      SECOM Sights ID パスワードのリセット
    </h2>
    <section class="forgotNewPasswordForm_caption">
      <h3 class="forgotNewPasswordForm_caption-text">
        新しいパスワードを登録します
      </h3>
    </section>
    <p class="forgotNewPasswordForm_desc">
      認証コードと新しいパスワードを入力してください。
    </p>
    <div v-if="errorMessage" class="forgotNewPasswordForm_error-wronginfo">
      {{ errorMessage }}
    </div>

    <ValidationObserver
      ref="observer"
      v-slot="{ invalid }"
      tag="div"
      class="forgotNewPasswordForm_content"
    >
      <section class="forgotNewPasswordForm_container">
        <ValidationProvider
          v-slot="{ errors }"
          vid="code"
          name="認証コード"
          class="forgotNewPasswordForm_input"
        >
          <h4
            class="forgotNewPasswordForm_container_item"
            :class="{
              ['forgotNewPasswordForm_container_item--error']: errors.length,
            }"
          >
            認証コード
          </h4>

          <app-text-field
            v-model="code"
            :maxlength="11"
            required
            class="forgotNewPasswordForm_container_input"
            :error-messages="errors"
            color="dark"
            full-width
            persistent-hint
            placeholder="999999"
          />
        </ValidationProvider>
        <ValidationProvider
          v-slot="{ errors }"
          vid="password"
          name="新しいパスワード"
          rules="passwordValidChars|minmax:8,20|passwordNumeric|passwordLowercase|passwordUppercase|symbol"
        >
          <h4
            class="forgotNewPasswordForm_container_item"
            :class="{
              ['forgotNewPasswordForm_container_item--error']: errors.length,
            }"
          >
            新しいパスワード
          </h4>

          <app-text-field
            ref="password"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            :append-icon="
              showPassword ? 'icon icon-eye' : 'icon icon-eye-closed'
            "
            class="forgotNewPasswordForm_container_input"
            :error-messages="errors"
            color="dark"
            required
            persistent-hint
            aria-autocomplete="off"
            autocomplete="off"
            full-width
            hint="半角英数字記号8文字以上"
            @click:append="showPassword = !showPassword"
          />
        </ValidationProvider>
        <ValidationProvider
          v-slot="{ errors }"
          vid="confirmPassword"
          name="新しいパスワード（確認用）"
          rules="password:@password"
        >
          <h4
            class="forgotNewPasswordForm_container_item"
            :class="{
              ['forgotNewPasswordForm_container_item--error']: errors.length,
            }"
          >
            新しいパスワード（確認用）
          </h4>

          <app-text-field
            ref="confirmPassword"
            v-model="confirmPassword"
            :type="showConfirmPassword ? 'text' : 'password'"
            :append-icon="
              showConfirmPassword ? 'icon icon-eye' : 'icon icon-eye-closed'
            "
            class="forgotNewPasswordForm_container_input"
            :error-messages="errors"
            color="dark"
            required
            persistent-hint
            aria-autocomplete="off"
            autocomplete="off"
            full-width
            hint="確認の為、同じパスワードを再度入力してください。"
            @click:append="showConfirmPassword = !showConfirmPassword"
          />
        </ValidationProvider>
      </section>
      <div class="forgotNewPasswordForm_btn_container">
        <v-btn
          block
          depressed
          large
          color="primary"
          class="forgotNewPasswordForm_btn"
          :disabled="invalid || !allFieldsFilled"
          :loading="loading"
          @click="handleSubmit"
        >
          新しいパスワードを登録
        </v-btn>
      </div>
    </ValidationObserver>
  </v-card>
</template>

<script>
export default {
  name: 'ForgotNewPasswordForm',
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      password: null,
      confirmPassword: null,
      showPassword: false,
      showConfirmPassword: false,
      code: '',
    };
  },
  computed: {
    allFieldsFilled() {
      return !!(this.code && this.password && this.confirmPassword);
    },
  },
  watch: {
    errorMessage(val) {
      if (val) {
        this.$refs.observer.setErrors({ code: [''] });
      } else {
        this.$refs.observer.setErrors({ code: [] });
      }
    },
  },
  methods: {
    handleSubmit() {
      this.$emit('submit', {
        password: this.password,
        code: this.code,
      });
    },
    handleCancel() {
      this.$emit('cancel');
    },
  },
};
</script>

<style lang="scss">
.forgotNewPasswordForm {
  &_card {
    width: 500px;
    height: 556px;
    overflow: hidden;
    margin: 0 auto;
    margin-bottom: 30px;
    &.v-sheet.v-card:not(.v-sheet--outlined) {
      padding: 102px;
      padding-top: 30px;
      box-shadow: 0 3px 12px 0 rgba(0, 0, 0, 0.06);
      background-color: #fff;
      border-radius: 10px;
    }
    .v-btn:not(.v-btn--round).v-size--large {
      height: 48px;
      border-radius: 10px;
    }

    .v-btn__content {
      font-weight: bold;
    }
    .theme--light.v-btn.v-btn--disabled.v-btn--has-bg {
      background-color: #eeeeee !important;
    }
  }
  &_title {
    font-size: 16px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.44;
    letter-spacing: normal;
    text-align: center;
    color: #333;
  }
  &_desc {
    font-size: 12px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.33;
    letter-spacing: normal;
    text-align: left;
    color: #666;
  }
  &_caption {
    margin: 20px auto 29px;
    display: flex;
    align-items: center;
    justify-content: center;
    &-text {
      text-align: center;
      font-size: 14px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      color: #4b4b4b;
    }
    &-icon {
      margin-top: 3px;
    }
  }
  &_container {
    display: flex;
    flex-direction: column;
    &_item {
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      height: 16px;
      color: #333333;
      &--error {
        color: var(--v-error-base);
      }
    }
    &_input {
      width: 100%;
      margin-bottom: 20px;
      margin-top: 0;
      padding-top: 0;
      .v-input {
        font-size: 18px;
      }
      .v-text-field__slot {
        padding-left: 0;
      }
      .v-input__slot {
        min-height: 40px !important;
        .v-input__append-inner {
          margin-top: 9px;
        }
      }
      input {
        line-height: 32px !important;
        color: var(--text-color) !important;
        font-size: 18px !important;
        font-weight: normal !important;
        font-stretch: normal !important;
        font-style: normal;
        letter-spacing: normal;
        text-align: left;
        &:-webkit-autofill,
        &:-webkit-autofill::first-line {
          font-family: 'Noto Sans JP', sans-serif;
          font-size: 18px;
          font-weight: 500;
        }
      }
    }
  }

  &_input {
    margin-top: 4px;
    &-label {
      font-size: 14px;
      font-weight: 500;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: 0.7px;
      text-align: left;
      color: var(--text-color-2);
      margin-right: 20px;
    }
    &-code {
      &.v-text-field {
        max-width: 168px;
        font-size: 40px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: 2px;
        text-align: center;
        color: var(--text-color);
        &.theme--light.v-input input {
          max-width: 168px;
          max-height: 70px;
          text-align: center;
        }
      }
    }
  }

  &_btn {
    &_container {
      text-align: center;
      margin-bottom: 20px;
    }
  }
  &_content {
    display: flex;
    margin: 0 auto;
    justify-content: center;
    flex-direction: column;
    form {
      width: 100%;
      padding: 0 40px;
    }
    &-item {
      p {
        font-size: 14px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
        text-align: center;
        color: #666;
      }
    }
  }

  &_error {
    &-wronginfo {
      height: 5px;
      font-size: 15px;
      font-weight: normal;
      line-height: 1.4;
      text-align: left;
      color: var(--error-color);
      margin: 0 auto;
      margin-top: -10px;
      margin-bottom: 45px;
      text-align: center;
    }
    &-gap {
      height: 30px;
    }
  }
}
</style>
